import React, { useMemo } from "react";

interface WavyCircleProps {
  dotCount?: number;
  size?: number; // overall rendered size in px (optional)
}

const WavyCircle: React.FC<WavyCircleProps> = ({ dotCount = 28, size = 120 }) => {
  // center and sizes tuned for a small 120x120 viewBox
  const viewSize = 140;
  const centerX = viewSize / 2;
  const centerY = viewSize / 2;
  const baseRadius = 50; // main circular radius for waves

  const scatteredDots = useMemo(() => {
    return Array.from({ length: dotCount }, (_, i) => {
      const angle = Math.random() * 2 * Math.PI;
      const radius = 16 + Math.random() * 14; // keep inside smaller canvas
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      const r = 0.9 + Math.random() * 1.6;
      const delay = Math.floor(Math.random() * 1000);
      const duration = 1800 + Math.random() * 1600;

      return (
        <circle
          key={i}
          className="expand-rotate"
          cx={centerX}
          cy={centerY}
          r={r}
          fill="var(--accent)"
          style={
            {
              animationDelay: `${delay}ms`,
              animationDuration: `${duration}ms`,
              // store target translation as CSS vars used by keyframes
              "--target-x": `${(x - centerX).toFixed(2)}px`,
              "--target-y": `${(y - centerY).toFixed(2)}px`,
            } as React.CSSProperties
          }
        />
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dotCount]); // intentionally only dotCount dependency so positions are stable

  const createFlowingPath = (radiusOffset: number, curveIntensity: number, startAngle: number): string => {
    const points: string[] = [];
    const segments = 20;

    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2 + startAngle;
      const wave = Math.sin(angle * 3) * curveIntensity;
      const radius = baseRadius + radiusOffset + wave;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);

      if (i === 0) {
        points.push(`M ${x.toFixed(2)},${y.toFixed(2)}`);
      } else {
        const prevAngle = ((i - 1) / segments) * Math.PI * 2 + startAngle;
        const prevWave = Math.sin(prevAngle * 3) * curveIntensity;
        const prevRadius = baseRadius + radiusOffset + prevWave;

        const cp1x = centerX + prevRadius * Math.cos(prevAngle + 0.12);
        const cp1y = centerY + prevRadius * Math.sin(prevAngle + 0.12);
        const cp2x = centerX + radius * Math.cos(angle - 0.12);
        const cp2y = centerY + radius * Math.sin(angle - 0.12);

        points.push(
          `C ${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${x.toFixed(2)},${y.toFixed(2)}`
        );
      }
    }

    return points.join(" ");
  };

  return (
    <div className="flex items-center justify-center bg-transparent" style={{ width: size+10, height: size+20 }}>
      <div className="relative w-full h-full overflow-visible">
        <svg
          className="w-full h-full"
          viewBox={`0 0 ${viewSize} ${viewSize}`}
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-labelledby="titleId"
        >
          {/* <title id="titleId">Animated Decorative Wavy Circle (small)</title> */}

          <defs>
            <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="var(--bubble-own-from)" />
              <stop offset="100%" stopColor="var(--bubble-own-to)" />
            </linearGradient>
            <linearGradient id="grad2" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="var(--accent)" />
              <stop offset="100%" stopColor="var(--accent)" />
            </linearGradient>
            <linearGradient id="grad3" x1="0%" y1="50%" x2="100%" y2="50%">
              <stop offset="0%" stopColor="var(--bubble-own-from)" />
              <stop offset="100%" stopColor="var(--bubble-own-to)" />
            </linearGradient>
            <linearGradient id="grad4" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="var(--bubble-own-from)" />
              <stop offset="50%" stopColor="var(--accent)" />
              <stop offset="100%" stopColor="var(--bubble-own-to)" />
            </linearGradient>
            <linearGradient id="grad5" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="var(--bubble-own-from)" />
              <stop offset="30%" stopColor="var(--accent)" />
              <stop offset="70%" stopColor="var(--bubble-own-to)" />
              <stop offset="100%" stopColor="var(--accent)" />
            </linearGradient>
          </defs>

          {/* Dots */}
          <g>{scatteredDots}</g>

          {/* Flowing rings */}
          <g className="spin">
            <path
              d={createFlowingPath(10, 5, 0)}
              fill="none"
              stroke="url(#grad1)"
              strokeWidth="1.0"
              strokeLinecap="round"
              opacity="0.95"
            />
          </g>

          <g className="spin-reverse">
            <path
              d={createFlowingPath(6, 6, Math.PI / 5)}
              fill="none"
              stroke="url(#grad2)"
              strokeWidth="1.1"
              strokeLinecap="round"
              opacity="0.85"
            />
          </g>

          <g className="spin">
            <path
              d={createFlowingPath(2, 7, Math.PI / 3)}
              fill="none"
              stroke="url(#grad3)"
              strokeWidth="1.0"
              strokeLinecap="round"
              opacity="0.75"
            />
          </g>

          <g className="spin-reverse">
            <path
              d={createFlowingPath(-3, 5.5, Math.PI / 2)}
              fill="none"
              stroke="url(#grad5)"
              strokeWidth="0.9"
              strokeLinecap="round"
              opacity="0.6"
            />
          </g>
        </svg>

        {/* Embedded styles */}
        <style>{`
          /* Make transforms work properly for SVG elements */
          .expand-rotate,
          .spin,
          .spin-reverse {
            transform-box: fill-box;
            transform-origin: center;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes spinReverse {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(-360deg); }
          }
          @keyframes expandRotate {
            0% {
              transform: translate(0, 0) scale(0.3) rotate(0deg);
              opacity: 0.2;
            }
            50% {
              opacity: 1;
              transform: translate(calc(var(--target-x) * 0.5), calc(var(--target-y) * 0.5)) scale(0.8) rotate(180deg);
            }
            100% {
              transform: translate(var(--target-x), var(--target-y)) scale(1.2) rotate(360deg);
              opacity: 0.1;
            }
          }
          .expand-rotate {
            animation: expandRotate ease-out infinite;
            will-change: transform, opacity;
          }
          .spin {
            animation: spin 25s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }
          .spin-reverse {
            animation: spinReverse 32s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }
        `}</style>
      </div>
    </div>
  );
};

export default WavyCircle;
